import { useSearchParams } from 'react-router-dom';
import { Gate } from '../../../global/contexts/auth/auth';
import { useProjects, useRepositories } from '../../../global/contexts/lookups/lookups';

import { Tag } from '../../../common/components/detail/detail';
import { useDurations, useTaskMetrics, useMetricTasks } from '../contexts/coordination';
import { FIXTURE_SCOPE, onFixtures } from '../queries/source';
import {
    TASK_STATE_LABEL,
    humanSeconds,
    label,
    unknownBreakdown,
    unknownLabel,
} from '../utils/format';
import { PAGE_SIZE, durationFilters, pageOffset, positiveId } from '../utils/operator';
import {
    Fact,
    Facts,
    Moment,
    Origin,
    PageControls,
    RawJson,
    RefreshStatus,
    TechnicalDetails,
    Unavailable,
    Unknown,
} from './bits';

// TIEMPOS — cuánto tardó cada cosa, y nunca cuánto rindió nadie.
//
// Las cuatro reglas que esta pantalla no puede romper, porque romperlas es
// mentir sobre lo que el sistema sabe:
//
//   1. `null` NO ES CERO. Una medida sin dato, fuera de orden o todavía abierta
//      vale `null` con su razón, no se dibuja como 0 y no entra en ningún
//      promedio. Cada agregación dice su población, cuánto pudo medir y por qué
//      no pudo el resto: los tres números salen juntos.
//   2. LO SUPERPUESTO NO SE SUMA. Dos corridas encimadas traen `elapsed_union`
//      además del total, y los dos viajan juntos porque contestan cosas
//      distintas.
//   3. NADA SE LLAMA «PRODUCTIVIDAD». Cada medida es la distancia entre dos
//      marcas del reloj del servidor. No dice que alguien estuviera trabajando.
//   4. LO QUE NO ES EJECUCIÓN VA APARTE: esperas, devoluciones, pausas e
//      interrupciones viajan en bloques separados y no se suman con ella.

const BLOCKS = [
    [
        'attempts',
        'Ejecución declarada',
        'La abre y la cierra el agente. Mastermind no mide el proceso.',
    ],
    [
        'waits',
        'Espera de decisión',
        'De la entrega a la decisión. Nadie está corriendo nada en este tiempo.',
    ],
    ['returns', 'Devoluciones', 'La vuelta atrás y lo que tardó en volver a entregarse.'],
    [
        'interruptions',
        'Interrupciones',
        'Cuánto tardó la SOLICITUD en quedar resuelta. No es lo que tardó en cortarse.',
    ],
    [
        'pauses',
        'Pausas',
        'La cola dejó de entregarle trabajo a un puesto. Es del puesto, no de la tarea.',
    ],
];

export default function MetricsScreen() {
    const [params, setParams] = useSearchParams();
    const view = params.get('vista') === 'tarea' ? 'tarea' : 'agregado';
    const projectId = positiveId(params.get('proyecto'));
    const repositoryId = positiveId(params.get('repo'));
    const taskId = onFixtures ? FIXTURE_SCOPE.taskId : positiveId(params.get('tarea'));
    const offset = pageOffset(params.get('tareas_desde'));
    const projects = useProjects();
    const repositories = useRepositories();
    const tasks = useMetricTasks({ projectId, repositoryId, offset, enabled: view === 'tarea' });
    const change = (key, value) => {
        const next = new URLSearchParams(params);
        value ? next.set(key, value) : next.delete(key);
        if (key === 'proyecto' || key === 'repo') {
            next.delete('tarea');
            next.delete('tareas_desde');
        }
        if (key === 'proyecto') next.delete('repo');
        setParams(next);
    };
    return (
        <section className="coord">
            <header className="coord__head">
                <h2 className="coord__title">Tiempos del trabajo</h2>
            </header>
            <p className="coord__about">
                Consulta cuánto tarda cada etapa. Los tiempos desconocidos no cuentan como cero; la
                espera y la ejecución se muestran por separado.
            </p>
            <div className="coord__filters" role="group" aria-label="Alcance de la medida">
                {[
                    ['agregado', 'Resumen'],
                    ['tarea', 'Por tarea'],
                ].map(([key, text]) => (
                    <button
                        key={key}
                        type="button"
                        className={'coord__chip' + (view === key ? ' coord__chip--on' : '')}
                        aria-pressed={view === key}
                        onClick={() => change('vista', key)}
                    >
                        {text}
                    </button>
                ))}
            </div>
            <div className="coord__toolbar">
                <Gate domain="projects:read">
                    <label className="coord__field">
                        <span>Proyecto</span>
                        <select
                            className="coord__select"
                            value={projectId ?? ''}
                            disabled={onFixtures}
                            onChange={e => change('proyecto', e.target.value)}
                        >
                            <option value="">Todos los proyectos</option>
                            {(projects.data ?? []).map(project => (
                                <option key={project.id} value={project.id}>
                                    {project.name}
                                </option>
                            ))}
                        </select>
                    </label>
                </Gate>
                <Gate domain="repositories:read">
                    <label className="coord__field">
                        <span>Repositorio</span>
                        <select
                            className="coord__select"
                            value={repositoryId ?? ''}
                            disabled={onFixtures}
                            onChange={e => change('repo', e.target.value)}
                        >
                            <option value="">Todos los repositorios</option>
                            {(repositories.data ?? [])
                                .filter(repo => !projectId || repo.project_id === projectId)
                                .map(repo => (
                                    <option key={repo.id} value={repo.id}>
                                        {repo.name}
                                    </option>
                                ))}
                        </select>
                    </label>
                </Gate>
            </div>
            {onFixtures && (
                <p className="coord__panel-note">El ejemplo muestra un alcance fijo de prueba.</p>
            )}
            {view === 'tarea' && !onFixtures && (
                <>
                    <Gate
                        domain="tasks:read"
                        fallback={
                            <p className="coord__empty">
                                Abre un enlace de métricas de una tarea para consultar su desglose.
                                Tu cuenta no puede listar tareas.
                            </p>
                        }
                    >
                        <label className="coord__field">
                            <span>Tarea</span>
                            <select
                                className="coord__select"
                                value={taskId ?? ''}
                                disabled={tasks.isFetching}
                                onChange={e => change('tarea', e.target.value)}
                            >
                                <option value="">Selecciona una tarea</option>
                                {taskId &&
                                    !(tasks.data?.items ?? []).some(task => task.id === taskId) && (
                                        <option value={taskId}>Tarea #{taskId}</option>
                                    )}
                                {(tasks.data?.items ?? []).map(task => (
                                    <option key={task.id} value={task.id}>
                                        #{task.id} · {task.title}
                                    </option>
                                ))}
                            </select>
                        </label>
                        {tasks.isLoading && <p role="status">Buscando tareas…</p>}
                        {tasks.error && (
                            <p role="alert" className="coord__error">
                                {tasks.error.message}
                            </p>
                        )}
                        {tasks.data?.pagination.total === 0 && (
                            <p className="coord__empty">No hay tareas en este alcance.</p>
                        )}
                        <PageControls
                            total={tasks.data?.pagination.total}
                            offset={offset}
                            limit={PAGE_SIZE}
                            onChange={value => change('tareas_desde', value)}
                            busy={tasks.isFetching}
                        />
                    </Gate>
                </>
            )}
            {view === 'tarea' ? (
                taskId ? (
                    <TaskMetrics taskId={taskId} />
                ) : (
                    <p className="coord__empty">
                        Elige una tarea para ver su tiempo de ejecución y sus revisiones.
                    </p>
                )
            ) : (
                <Durations filters={durationFilters(projectId, repositoryId)} />
            )}
        </section>
    );
}

function TaskMetrics({ taskId }) {
    const query = useTaskMetrics(taskId);
    const { data, origin, unavailable, isLoading, error } = query;

    return (
        <>
            <RefreshStatus query={query} />
            <Origin origin={origin} />
            {isLoading && <p className="coord__loading">Leyendo los tiempos...</p>}
            {error && <p className="coord__error">{error.message}</p>}
            {unavailable && <Unavailable>{unavailable}</Unavailable>}

            {data && (
                <>
                    <header className="coord__panel">
                        <h3>
                            #{data.task.id} · {data.task.title}
                        </h3>
                        <Facts>
                            <Fact label="Estado">
                                {label(TASK_STATE_LABEL, data.task.workflow_state)}
                            </Fact>
                            <Fact label="Reservada">
                                {data.task.claimed_at ? (
                                    <Moment
                                        at={data.task.claimed_at}
                                        observedAt={data.observed_at}
                                    />
                                ) : (
                                    <Unknown>sin reserva</Unknown>
                                )}
                            </Fact>
                        </Facts>
                    </header>

                    <TechnicalDetails label="Cómo se calculan los tiempos">
                        <Meaning meaning={data.meaning} />
                    </TechnicalDetails>

                    {BLOCKS.map(([key, title, note]) => (
                        <Block key={key} title={title} note={note} block={data[key]} showItems />
                    ))}

                    <Retransmissions value={data.retransmissions} />
                    <Usage usage={data.usage} />
                    <TechnicalDetails>
                        <Sources sources={data.sources} families={data.families} />
                    </TechnicalDetails>
                    <RawJson value={data} label="Ver el desglose como llegó" />
                </>
            )}
        </>
    );
}

function Durations({ filters }) {
    const query = useDurations(onFixtures ? {} : filters);
    const { data, origin, unavailable, isLoading, error } = query;

    return (
        <>
            <RefreshStatus query={query} />
            <Origin origin={origin} />
            {isLoading && <p className="coord__loading">Leyendo el agregado...</p>}
            {error && <p className="coord__error">{error.message}</p>}
            {unavailable && <Unavailable>{unavailable}</Unavailable>}

            {data && (
                <>
                    <p className="coord__panel-note">Resumen del alcance seleccionado arriba.</p>

                    <TechnicalDetails label="Cómo se calculan los tiempos">
                        <Meaning meaning={data.meaning} />
                    </TechnicalDetails>

                    {BLOCKS.map(([key, title, note]) => (
                        <Block key={key} title={title} note={note} block={data[key]} />
                    ))}

                    <Retransmissions value={data.retransmissions} />
                    <Usage usage={data.usage} />
                    <TechnicalDetails>
                        <Sources sources={data.sources} families={data.families} />
                    </TechnicalDetails>
                    <RawJson value={data} label="Ver el agregado como llegó" />
                </>
            )}
        </>
    );
}

// Las cinco frases que el contrato manda con cada respuesta. Se muestran: un
// demo que las tira invita exactamente la lectura que fueron escritas para
// impedir.
function Meaning({ meaning }) {
    return (
        <section className="coord__meaning">
            <h3>Qué miden estos números, y qué no</h3>
            <ul>
                {Object.entries(meaning).map(([key, text]) => (
                    <li key={key}>
                        <code>{key}</code>
                        <span>{text}</span>
                    </li>
                ))}
            </ul>
        </section>
    );
}

function Block({ title, note, block, showItems = false }) {
    if (!block) return null;
    if (block.total === 0)
        return (
            <section className="coord__panel">
                <h3>{title}</h3>
                <p className="coord__panel-note">
                    No hay registros de esta etapa en el alcance seleccionado.
                </p>
            </section>
        );

    return (
        <section className="coord__panel">
            <header className="coord__panel-head">
                <h3>{title}</h3>
                <span className="coord__panel-count">{block.total} medida(s)</span>
            </header>
            <p className="coord__panel-note">{note}</p>
            {block.truncated && (
                <p className="coord__panel-note">
                    <strong>La lista se cortó:</strong> hay más renglones de los que viajaron.
                </p>
            )}

            <Aggregation title="Resumen" summary={block.summary} />
            {block.cycle && (
                <Aggregation title="De la toma a la entrega (ciclo)" summary={block.cycle} />
            )}
            {block.elapsed_union && (
                <Union union={block.elapsed_union} total={block.summary?.total_seconds} />
            )}

            {(block.interrupted != null || block.expired != null) && (
                <Facts>
                    <Fact label="Interrumpidas">{block.interrupted}</Fact>
                    <Fact label="Vencidas">{block.expired}</Fact>
                </Facts>
            )}

            {showItems && block.items?.length > 0 && (
                <TechnicalDetails label="Ver cada medición">
                    <ul className="coord__spans">
                        {block.items.map((item, index) => (
                            <Item key={index} item={item} />
                        ))}
                    </ul>
                </TechnicalDetails>
            )}
        </section>
    );
}

// Población, medidas y desconocidas: los tres juntos o ninguno. Un promedio sin
// su población es un número que no se puede contradecir.
function Aggregation({ title, summary }) {
    if (!summary) return null;
    const unknown = unknownBreakdown(summary.unknown);

    return (
        <div className="coord__aggregation">
            <h4>{title}</h4>
            <p className="coord__aggregation-population">
                <strong>{summary.population}</strong> en la población ·{' '}
                <strong>{summary.known}</strong> medidas ·{' '}
                {unknown ? (
                    <Unknown>
                        {summary.population - summary.known} sin medir: {unknown}
                    </Unknown>
                ) : (
                    'ninguna sin medir'
                )}
            </p>
            <dl className="coord__stats">
                {[
                    ['total', summary.total_seconds],
                    ['promedio', summary.avg_seconds],
                    ['mínimo', summary.min_seconds],
                    ['mediana', summary.p50_seconds],
                    ['percentil 90', summary.p90_seconds],
                    ['máximo', summary.max_seconds],
                ].map(([name, value]) => (
                    <div key={name}>
                        <dt>{name}</dt>
                        <dd title={value == null ? 'sin dato' : `${value} s`}>
                            {value == null ? <Unknown /> : humanSeconds(value)}
                        </dd>
                    </div>
                ))}
            </dl>
            {summary.known === 0 && summary.population > 0 && (
                <p className="coord__panel-note">
                    Ninguna se pudo medir, así que no hay promedio. <strong>No es cero</strong>:
                    faltan marcas válidas para calcularlo.
                </p>
            )}
        </div>
    );
}

// La unión viaja al lado del total, no en su lugar: dos que se superponen no
// pasaron dos veces, y la suma de lo encimado no es nada.
function Union({ union, total }) {
    return (
        <div className="coord__union">
            <h4>Unión y suma, los dos</h4>
            <dl className="coord__stats">
                <div>
                    <dt>suma</dt>
                    <dd>{total == null ? <Unknown /> : humanSeconds(total)}</dd>
                </div>
                <div>
                    <dt>unión</dt>
                    <dd>{union.seconds == null ? <Unknown /> : humanSeconds(union.seconds)}</dd>
                </div>
                <div>
                    <dt>contadas</dt>
                    <dd>{union.counted}</dd>
                </div>
                <div>
                    <dt>superpuestas</dt>
                    <dd>{union.overlapped}</dd>
                </div>
                <div>
                    <dt>salteadas</dt>
                    <dd>{union.skipped}</dd>
                </div>
            </dl>
            <p className="coord__panel-note">{union.note}</p>
        </div>
    );
}

// Un renglón con sus marcas al lado de la medida, para que la cuenta se pueda
// rehacer: un número que nadie puede rederivar es un número que nadie puede
// contradecir.
function Item({ item }) {
    const marks = Object.entries(item.marks ?? {});
    return (
        <li className="coord__span">
            <header>
                <strong>{itemName(item)}</strong>
                {item.state && <Tag>{item.state}</Tag>}
                {item.open && <Tag tone="warn">abierta</Tag>}
                {item.provider && <Tag>{item.provider}</Tag>}
            </header>

            <dl className="coord__span-marks">
                {marks.map(([name, value]) => (
                    <div key={name}>
                        <dt>{name}</dt>
                        <dd>{value ? <Moment at={value} /> : <Unknown />}</dd>
                    </div>
                ))}
            </dl>

            <ul className="coord__span-list">
                {Object.entries(item.spans ?? {}).map(([name, span]) => (
                    <li key={name}>
                        <span className="coord__span-name">{name}</span>
                        <span className="coord__span-family">{span.family}</span>
                        <span className="coord__span-value">
                            {span.known ? (
                                humanSeconds(span.seconds)
                            ) : (
                                <Unknown reason={span.reason}>{unknownLabel(span.reason)}</Unknown>
                            )}
                        </span>
                    </li>
                ))}
            </ul>

            {item.clock && (
                <p className="coord__span-clock">
                    Reloj del cliente aparte: el agente declaró{' '}
                    <code>{item.clock.reported_at ?? 'nada'}</code>, el servidor vio{' '}
                    <code>{item.clock.server_at ?? 'nada'}</code>, desfase{' '}
                    {item.clock.skew_seconds == null ? (
                        <Unknown reason={item.clock.reason} />
                    ) : (
                        `${item.clock.skew_seconds} s`
                    )}
                    . Ninguna duración se calcula con la hora que declaró el agente.
                </p>
            )}
        </li>
    );
}

function itemName(item) {
    const parts = [];
    if (item.review_round_id != null) parts.push(`ronda #${item.review_round_id}`);
    if (item.order_id != null) parts.push(`orden #${item.order_id}`);
    if (item.attempt_id != null) parts.push(`corrida #${item.attempt_id}`);
    if (item.task_id != null) parts.push(`tarea #${item.task_id}`);
    return parts.join(' · ');
}

function Retransmissions({ value }) {
    if (!value) return null;
    return (
        <section className="coord__panel">
            <h3>Reenvíos</h3>
            <Facts>
                <Fact label="Órdenes">{value.orders}</Fact>
                <Fact label="Reenviadas">{value.resent_orders}</Fact>
                <Fact label="Reenvíos">{value.resends}</Fact>
            </Facts>
            <p className="coord__panel-note">{value.note}</p>
        </section>
    );
}

// El consumo del proveedor no tiene fuente. Se dice; no se dibuja un cero.
function Usage({ usage }) {
    if (!usage) return null;
    return (
        <section className="coord__panel">
            <h3>Consumo del proveedor</h3>
            <p>
                <Unknown reason={usage.reason}>no se sabe</Unknown>
            </p>
            <p className="coord__panel-note">{usage.detail}</p>
            <Facts>
                <Fact label="Proveedores vistos">{usage.providers.join(' · ') || '—'}</Fact>
                <Fact label="Fuentes conocidas">
                    {usage.known_sources.length ? (
                        usage.known_sources.join(' · ')
                    ) : (
                        <Unknown>ninguna</Unknown>
                    )}
                </Fact>
            </Facts>
        </section>
    );
}

// De qué columna sale cada punta de cada medida. Va en la pantalla porque es lo
// que permite discutir un número en vez de creerlo.
function Sources({ sources, families }) {
    return (
        <section className="coord__panel">
            <h3>De dónde sale cada medida</h3>
            <ul className="coord__sources">
                {Object.entries(sources).map(([name, source]) => (
                    <li key={name}>
                        <strong>{name}</strong>
                        <span className="coord__sources-family">
                            {source.family} — {families[source.family]}
                        </span>
                        <code>
                            {source.from} → {source.to}
                        </code>
                        <p>{source.note}</p>
                    </li>
                ))}
            </ul>
        </section>
    );
}
